/**
 * 메인/수업 프레젠테이션 창 열기 — 듀얼 모니터면 다른 화면에 띄운다.
 *
 * Window Management API (Chrome/Edge 100+):
 * - `window.screen.isExtended` (권한 없이) 로 확장 모니터 여부 확인
 * - `window.getScreenDetails()` (권한 'window-management') 로 화면 좌표 획득
 *
 * 팝업 차단 때문에 `window.open`은 항상 클릭 직후 동기로 호출한다.
 * - 권한 허용 + 화면 정보 캐시가 있으면: 처음부터 다른 화면 크기·위치로 팝업 창을 연다.
 * - 아직 권한을 묻기 전이면: 현재 화면에 팝업 창을 먼저 연 뒤, 권한 응답에 따라
 *   다른 화면으로 옮기거나(moveTo/resizeTo) 현재 화면에 최대 크기로 맞춘다.
 * - 미지원 브라우저 / 단일 모니터 / 권한 거부: 기존처럼 새 탭.
 *   (탭은 화면 이동이 불가능해서 다른 화면으로 보낼 땐 별도 창(popup)이 된다)
 */

type ScreenDetailedLike = {
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
  left?: number;
  top?: number;
  isPrimary?: boolean;
};

type ScreenDetailsLike = {
  screens: ScreenDetailedLike[];
  currentScreen: ScreenDetailedLike;
};

type WindowWithScreenDetails = Window & {
  getScreenDetails?: () => Promise<ScreenDetailsLike>;
};

type PermissionStateLike = 'granted' | 'denied' | 'prompt' | 'unknown';

let cachedDetails: ScreenDetailsLike | null = null;
let permissionState: PermissionStateLike = 'unknown';
let primePromise: Promise<void> | null = null;

function getWin(): WindowWithScreenDetails | null {
  return typeof window !== 'undefined' ? (window as WindowWithScreenDetails) : null;
}

function isApiSupported(): boolean {
  const w = getWin();
  return !!w && typeof w.getScreenDetails === 'function';
}

/** true/false: 확장 모니터 여부, null: 알 수 없음(속성 미지원) */
function readIsExtended(): boolean | null {
  const w = getWin();
  const v = (w?.screen as (Screen & { isExtended?: boolean }) | undefined)?.isExtended;
  return typeof v === 'boolean' ? v : null;
}

function watchPermission(status: PermissionStatus): void {
  try {
    status.onchange = () => {
      permissionState = status.state as PermissionStateLike;
      if (permissionState !== 'granted') {
        cachedDetails = null;
      } else if (!cachedDetails) {
        // 사이트 설정에서 나중에 허용한 경우: 다음 클릭부터 바로 다른 화면에 열리도록 미리 받아 둔다.
        void getWin()
          ?.getScreenDetails?.()
          .then((d) => {
            cachedDetails = d ?? null;
          })
          .catch(() => undefined);
      }
    };
  } catch {
    /* ignore */
  }
}

async function queryPermission(): Promise<PermissionStateLike> {
  const perms = typeof navigator !== 'undefined' ? navigator.permissions : undefined;
  if (!perms?.query) return 'unknown';
  for (const name of ['window-management', 'window-placement']) {
    try {
      const status = await perms.query({ name } as unknown as PermissionDescriptor);
      const s = status.state as PermissionStateLike;
      watchPermission(status);
      return s;
    } catch {
      /* 다음 이름 시도 */
    }
  }
  return 'unknown';
}

/** 권한이 이미 허용돼 있으면 (프롬프트 없이) 화면 정보를 미리 받아 둔다. */
export function primeOtherScreenDetails(): Promise<void> {
  if (primePromise) return primePromise;
  primePromise = (async () => {
    if (!isApiSupported()) return;
    permissionState = await queryPermission();
    if (permissionState !== 'granted') return;
    try {
      cachedDetails = (await getWin()!.getScreenDetails!()) ?? null;
    } catch {
      cachedDetails = null;
    }
  })();
  return primePromise;
}

function sameScreen(a: ScreenDetailedLike, b: ScreenDetailedLike): boolean {
  return (
    a === b ||
    (a.availLeft === b.availLeft &&
      a.availTop === b.availTop &&
      a.availWidth === b.availWidth &&
      a.availHeight === b.availHeight)
  );
}

function pickOtherScreen(details: ScreenDetailsLike | null): ScreenDetailedLike | null {
  if (!details || !Array.isArray(details.screens) || details.screens.length < 2) return null;
  const current = details.currentScreen;
  const others = details.screens.filter((s) => !current || !sameScreen(s, current));
  if (others.length === 0) return null;
  // 보조(비주 모니터) 우선 → 없으면 첫 번째 다른 화면
  return others.find((s) => s.isPrimary === false) ?? others[0];
}

function featuresFor(rect: { left: number; top: number; width: number; height: number }): string {
  const r = (n: number) => Math.round(n);
  return `popup=yes,left=${r(rect.left)},top=${r(rect.top)},width=${r(rect.width)},height=${r(rect.height)}`;
}

function rectOfScreen(s: ScreenDetailedLike) {
  return { left: s.availLeft, top: s.availTop, width: s.availWidth, height: s.availHeight };
}

function currentScreenRect() {
  const w = getWin()!;
  const sc = w.screen as Screen & { availLeft?: number; availTop?: number };
  return {
    left: typeof sc.availLeft === 'number' ? sc.availLeft : 0,
    top: typeof sc.availTop === 'number' ? sc.availTop : 0,
    width: sc.availWidth || w.outerWidth || 1280,
    height: sc.availHeight || w.outerHeight || 800,
  };
}

function detachOpener(win: Window): void {
  try {
    win.opener = null;
  } catch {
    /* ignore */
  }
}

function placeWindow(win: Window, rect: { left: number; top: number; width: number; height: number }): void {
  try {
    if (win.closed) return;
    win.moveTo(Math.round(rect.left), Math.round(rect.top));
    win.resizeTo(Math.round(rect.width), Math.round(rect.height));
  } catch {
    /* ignore */
  }
}

function openAsTab(url: string): Window | null {
  try {
    const w = window.open(url, '_blank');
    if (!w) return null;
    detachOpener(w);
    return w;
  } catch {
    return null;
  }
}

let openHereOnce = false;

/** 다음 한 번은 보조 모니터 처리 없이 기존처럼(새 탭) 연다 — 앱 확인 모달의 '이 화면에서 열기'. */
export function setOpenPresentationHereOnce(): void {
  openHereOnce = true;
}

export type PresentationScreenStatus = 'unsupported' | 'single' | 'granted' | 'prompt' | 'denied' | 'unknown';

/** 동기: 지금 알고 있는 상태(권한은 앱 시작 시 미리 조회해 둔 값). */
export function getPresentationScreenStatus(): PresentationScreenStatus {
  if (!isApiSupported()) return 'unsupported';
  const ext = readIsExtended();
  if (ext === false) return 'single';
  if (permissionState === 'granted') return 'granted';
  if (permissionState === 'denied') return 'denied';
  if (permissionState === 'prompt') return ext === true ? 'prompt' : 'unknown';
  return 'unknown';
}

/** 권한 상태를 다시 조회(사이트 설정을 바꾼 뒤 등). */
export async function refreshPresentationScreenStatus(): Promise<PresentationScreenStatus> {
  if (isApiSupported()) {
    permissionState = await queryPermission();
    if (permissionState === 'granted' && !cachedDetails) {
      try {
        cachedDetails = (await getWin()!.getScreenDetails!()) ?? null;
      } catch {
        cachedDetails = null;
      }
    }
  }
  return getPresentationScreenStatus();
}

/** 클릭 핸들러 안에서 호출: 브라우저 '창 관리' 권한 프롬프트를 띄운다. 허용되면 true. */
export async function requestPresentationScreenPermission(): Promise<boolean> {
  const w = getWin();
  if (!w?.getScreenDetails) return false;
  try {
    cachedDetails = (await w.getScreenDetails()) ?? null;
    permissionState = 'granted';
    return true;
  } catch {
    permissionState = await queryPermission().catch(() => 'unknown' as PermissionStateLike);
    return false;
  }
}

/**
 * 프레젠테이션용 창을 연다. 반드시 클릭 핸들러 안에서 동기로 호출할 것.
 * 반환값이 null이면 팝업 차단(기존 `openSameOriginWindow`와 같은 의미).
 */
export function openPresentationWindow(url: string): Window | null {
  const w = getWin();
  if (!w) return null;

  if (openHereOnce) {
    openHereOnce = false;
    return openAsTab(url);
  }
  if (!isApiSupported()) return openAsTab(url);

  const isExtended = readIsExtended();
  if (isExtended === false) return openAsTab(url);

  // 1) 권한 허용 + 화면 정보 확보: 바로 다른 화면에 연다.
  if (permissionState === 'granted' && cachedDetails) {
    const other = pickOtherScreen(cachedDetails);
    if (!other) return openAsTab(url);
    let win: Window | null = null;
    try {
      win = window.open(url, '_blank', featuresFor(rectOfScreen(other)));
    } catch {
      win = null;
    }
    if (!win) return null;
    detachOpener(win);
    // 일부 브라우저는 features 좌표를 무시하므로 한 번 더 맞춘다.
    placeWindow(win, rectOfScreen(other));
    return win;
  }

  // 2) 권한 거부: 기존 새 탭.
  if (permissionState === 'denied') return openAsTab(url);

  // 3) 아직 모름/미요청 + 확장 모니터(또는 판별 불가): 현재 화면 오른쪽 절반에 창을 먼저 열고,
  //    권한 요청 결과에 따라 다른 화면으로 옮긴다. (팝업 차단 회피 위해 open이 먼저)
  if (isExtended !== true) return openAsTab(url);
  // 권한 요청(getScreenDetails)은 사용자 클릭 활성화가 필요한데, 일반 크롬 탭에서는
  // window.open이 그 활성화를 소모해 버려 프롬프트가 뜨지 않고 거부될 수 있다.
  // → window.open보다 먼저 요청을 시작해 둔다(결과는 창을 연 뒤에 반영).
  let detailsPromise: Promise<ScreenDetailsLike> | null = null;
  try {
    detailsPromise = w.getScreenDetails!();
  } catch {
    detailsPromise = null;
  }
  const cur = currentScreenRect();
  const initial = {
    left: cur.left + Math.round(cur.width / 2),
    top: cur.top,
    width: Math.round(cur.width / 2),
    height: cur.height,
  };
  let win: Window | null = null;
  try {
    win = window.open(url, '_blank', featuresFor(initial));
  } catch {
    win = null;
  }
  if (!win) return null;
  detachOpener(win);
  const opened = win;
  void (async () => {
    try {
      if (!detailsPromise) throw new Error('no-details');
      const details = await detailsPromise;
      cachedDetails = details ?? null;
      permissionState = 'granted';
      const other = pickOtherScreen(cachedDetails);
      placeWindow(opened, other ? rectOfScreen(other) : cur);
    } catch {
      // 거부/닫힘: 현재 화면에 최대 크기로
      permissionState = await queryPermission().catch(() => 'unknown' as PermissionStateLike);
      placeWindow(opened, cur);
    }
  })();
  return win;
}

if (typeof window !== 'undefined') {
  void primeOtherScreenDetails();
}
